import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { portalColors } from './portalStyles';
import { PortalHeader } from './PortalHeader';
import { PortalTabs, type PortalTab } from './PortalTabs';
import { TodayView } from './TodayView';
import { WeekView } from './WeekView';
import { MonthView } from './MonthView';
import { PortalFooter } from './PortalFooter';
import { ReceiptView } from './ReceiptView';
import { notifyOwner } from './useCrewLifecycle';
import { makePreviewClient, PreviewReadOnlyContext } from './previewClient';
import { PreviewBar, PREVIEW_BAR_HEIGHT } from './PreviewBar';
import type { CrewPortalData, CrewReceipt } from '../../types/crewPortal';

type PortalError = 'not_found' | 'deactivated' | 'network';

interface CrewPortalPageProps {
  /**
   * §N — 'preview' = /crew/preview/:userId: the owner's read-only preview-as-crew.
   * The access token rides in the URL fragment, is stripped before first paint,
   * and reaches Supabase only via a dedicated non-persisting client (D-e).
   * Read-only rendering keys on `preview === true` from the RESPONSE, not the URL.
   */
  mode?: 'live' | 'preview';
}

export function CrewPortalPage({ mode = 'live' }: CrewPortalPageProps) {
  const { slug, userId } = useParams<{ slug?: string; userId?: string }>();
  const isPreviewRoute = mode === 'preview';
  // §N — read + strip the fragment synchronously on first render (before paint);
  // the token lives only in this state slot and the preview client's header.
  const [previewToken] = useState<string | null>(() => {
    if (!isPreviewRoute) return null;
    const token = new URLSearchParams(window.location.hash.slice(1)).get('access_token');
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
    return token;
  });
  const [data, setData] = useState<CrewPortalData | null>(null);
  const [error, setError] = useState<PortalError | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<PortalTab>('today');
  // §K receipt view (?receipt={ref}); null = the normal portal
  const [receipt, setReceipt] = useState<CrewReceipt | null>(null);
  const receiptRef = new URLSearchParams(window.location.search).get('receipt');

  useEffect(() => {
    // §N — preview path: owner-scoped RPC, no receipt branch, no opened ping,
    // no sessionStorage key. not_authorized|no_portal collapse to not_found.
    if (isPreviewRoute) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if (!previewToken || !userId) { setError('not_found'); setLoading(false); return; }
      void (async () => {
        const { data: result, error: rpcErr } = await makePreviewClient(previewToken)
          .rpc('get_crew_portal_preview', { p_user_id: userId });
        if (rpcErr) { setError('network'); setLoading(false); return; }
        const parsed = result as CrewPortalData & { error?: string };
        if (parsed.error === 'not_authorized' || parsed.error === 'no_portal') { setError('not_found'); setLoading(false); return; }
        if (parsed.error) { setError('network'); setLoading(false); return; }
        setData(parsed);
        setLoading(false);
      })();
      return;
    }

    if (!slug) { setError('not_found'); setLoading(false); return; }

    void (async () => {
      // §K — the receipt view resolves by ref, gated to this slug's jobs
      if (receiptRef) {
        const { data: rec, error: recErr } = await supabase.rpc('crew_portal_receipt', { p_slug: slug, p_receipt_ref: receiptRef });
        if (!recErr && rec?.success) {
          setReceipt(rec.receipt as CrewReceipt);
          setLoading(false);
          return;
        }
        // fall through to the normal portal on a bad/foreign ref
        window.history.replaceState({}, '', window.location.pathname);
      }

      const { data: result, error: rpcErr } = await supabase.rpc('get_crew_portal', { p_slug: slug });
      if (rpcErr) { setError('network'); setLoading(false); return; }
      const parsed = result as CrewPortalData & { error?: string };
      if (parsed.error === 'not_found') { setError('not_found'); setLoading(false); return; }
      if (parsed.error === 'deactivated') { setError('deactivated'); setLoading(false); return; }
      setData(parsed);
      setLoading(false);

      // §K read receipt — one owner ping per session per slug (the RPC already
      // stamped opened_at; this is the push half)
      if (parsed.settings?.read_receipts && (parsed.today?.length ?? 0) > 0) {
        try {
          const key = `cb_crew_opened_${slug}`;
          if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, '1');
            notifyOwner(slug, 'opened', parsed.today[0].id);
          }
        } catch { /* sessionStorage unavailable — skip the ping */ }
      }
    })();
  }, [slug, receiptRef, isPreviewRoute, previewToken, userId]);

  // Document title + meta
  useEffect(() => {
    if (data) {
      document.title = `${data.user.first_name}'s Schedule — ${data.business.name}`;
    } else {
      document.title = 'Schedule Portal — CrewBit';
    }
    // noindex
    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'robots';
      document.head.appendChild(meta);
    }
    meta.content = 'noindex, nofollow';
  }, [data]);

  const accentColor = data?.business.accent_color ?? portalColors.defaultAccent;

  // Loading
  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div style={{
            width: 32, height: 32, border: `3px solid ${portalColors.divider}`,
            borderTopColor: accentColor, borderRadius: '50%',
            animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
          }} />
          <div style={{ fontSize: 14, color: portalColors.secondary }}>Loading your schedule...</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // Errors
  if (error) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: portalColors.muted, marginBottom: 16 }}>CrewBit</div>
          {error === 'not_found' && (
            <>
              <div style={{ fontSize: 18, fontWeight: 500, color: portalColors.primary, marginBottom: 8 }}>
                This page doesn't exist.
              </div>
              <div style={{ fontSize: 14, color: portalColors.secondary, marginBottom: 32 }}>
                If you received this link, ask your manager to send a new one.
              </div>
            </>
          )}
          {error === 'deactivated' && (
            <>
              <div style={{ fontSize: 18, fontWeight: 500, color: portalColors.primary, marginBottom: 8 }}>
                This schedule page is no longer active.
              </div>
              <div style={{ fontSize: 14, color: portalColors.secondary, marginBottom: 32 }}>
                Contact your manager for more information.
              </div>
            </>
          )}
          {error === 'network' && (
            <>
              <div style={{ fontSize: 18, fontWeight: 500, color: portalColors.primary, marginBottom: 8 }}>
                Something went wrong.
              </div>
              <div style={{ fontSize: 14, color: portalColors.secondary, marginBottom: 32 }}>
                Check your connection and try again.
              </div>
            </>
          )}
          <PortalFooter variant="crew" deepLinkUrl={null} />
        </div>
      </div>
    );
  }

  // §K — the receipt view replaces the portal body when ?receipt resolves
  if (receipt) {
    return (
      <div style={containerStyle}>
        <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <ReceiptView receipt={receipt} onDone={() => { window.location.href = window.location.pathname; }} />
      </div>
    );
  }

  if (!data) return null;

  // §N — read-only keyed on the response (D-f): no lifecycle (JobHeroCard takes
  // the checklist fallback), checklist non-interactive, footer CTA hidden.
  const readOnly = isPreviewRoute && data.preview === true;

  const deepLinkUrl = !readOnly && data.invite_code
    ? `https://crewbit.app/invite/${data.invite_code}`
    : null;

  const lifecycle = !readOnly && data.settings && slug ? {
    slug,
    settings: data.settings,
    businessName: data.business.name,
    businessLogoUrl: data.business.logo_url,
    onCompleted: () => { /* the card flips its own local state; history refreshes next load */ },
  } : undefined;

  return (
    <PreviewReadOnlyContext.Provider value={readOnly}>
    {readOnly && (
      <PreviewBar
        firstName={data.user.first_name}
        slug={data.portal_slug ?? ''}
        status={data.portal_status ?? 'live'}
      />
    )}
    <div style={readOnly ? { ...containerStyle, paddingTop: 24 + PREVIEW_BAR_HEIGHT } : containerStyle}>
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <PortalHeader
        firstName={data.user.first_name}
        businessName={data.business.name}
        logoUrl={data.business.logo_url}
        accentColor={accentColor}
      />

      {/* §M — the owner's welcome note, business voice, under the header */}
      {data.welcome_note ? (
        <div style={{
          background: '#fff',
          border: `1px solid ${portalColors.divider}`,
          borderLeft: `3px solid ${accentColor}`,
          borderRadius: 12,
          padding: '14px 16px',
          marginBottom: 20,
        }}>
          <div style={{ fontSize: 14, lineHeight: 1.55, color: portalColors.primary, whiteSpace: 'pre-wrap' }}>
            {data.welcome_note}
          </div>
          <div style={{ fontSize: 12, color: portalColors.secondary, marginTop: 8 }}>
            — {data.business.name}
          </div>
        </div>
      ) : null}

      <PortalTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'today' && (
        <TodayView jobs={data.today} accentColor={accentColor} firstName={data.user.first_name} lifecycle={lifecycle} />
      )}

      {activeTab === 'week' && (
        <WeekView
          todayJobs={data.today}
          upcomingJobs={data.upcoming}
          historyJobs={data.history}
          accentColor={accentColor}
          firstName={data.user.first_name}
        />
      )}

      {activeTab === 'month' && (
        <MonthView
          monthJobs={data.month_jobs}
          accentColor={accentColor}
          firstName={data.user.first_name}
        />
      )}

      <PortalFooter variant="crew" deepLinkUrl={deepLinkUrl} />
    </div>
    </PreviewReadOnlyContext.Provider>
  );
}

const containerStyle: React.CSSProperties = {
  maxWidth: 480,
  margin: '0 auto',
  minHeight: '100vh',
  padding: '24px 20px',
  background: portalColors.background,
  fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};
